import jwtDecode from 'jwt-decode';
import round from 'lodash/round';
import differenceInDays from 'date-fns/difference_in_days';
import differenceInHours from 'date-fns/difference_in_hours';
import differenceInMinutes from 'date-fns/difference_in_minutes';
import { format } from 'date-fns';
import cookies from 'js-cookie';
import { dateTimeFormat as defaultDateTimeFormat, sessionTokenCookiesName, minus, maxCountLoadingMoreBtn } from 'src/utils/config';
import { valueTypes as alertsValueTypes } from 'src/models/AlertsValueTypes';
import {
  IconVisa,
  IconMasterCard,
  IconAmericanExpress,
  IconDiscover,
  IconDinersClub,
  IconJCB,
  IconUnionPay,
  IconPayment,
} from 'Icons';
import { percentageIncreaseName, percentageDecreaseName } from 'src/models/AlertsConditionTypes';
import currenciesTypes from 'src/models/CurrenciesTypes';

/** Removes 'session_token' from localStorage */
export function removeSessionToken() {
  cookies.remove(sessionTokenCookiesName);
}

/** Checks for 'session' in cookies, takes jwt from it, parses and returns */
export function checkSessionToken() {
  const token = cookies.get(sessionTokenCookiesName);
  if (!token) {
    return null;
  }
  let decodedData;
  try {
    decodedData = jwtDecode(token);
  } catch (e) {
    removeSessionToken();
    return null;
  }
  return { token, decodedData };
}

// DEPRECATED: DON'T USE IT ANYMORE
// TODO change it to lodash/round
/**
 * Function rounding decimal place value
 * @param {Number} num - Somebody number.
 * @param {Number} scale - Number of decimal places.
 */
export function roundingValue(num, scale) {
  if (!(`${num}`).includes('e')) {
    return +(`${Math.round(`${num}e+${scale}`)}e-${scale}`);
  }
  const arr = (`${num}`).split('e');
  let sig = '';
  if (+arr[1] + scale > 0) {
    sig = '+';
  }
  return +(`${Math.round(`${+arr[0]}e${sig}${+arr[1] + scale}`)}e-${scale}`);
}

/** Returns true if period between dates is less than 24h, else returns false */
function diffLess24h(firstDate, lastDate) {
  return Math.abs(differenceInDays(firstDate, lastDate)) < 1;
}

/** Returns true if period between dates is 1h, else returns false */
function oneHourPassed(firstDate, lastDate) {
  return Math.abs(differenceInHours(firstDate, lastDate)) === 1;
}

/** Returns true if period between dates is less than 1h, else returns false */
function diffLess1h(firstDate, lastDate) {
  return Math.abs(differenceInHours(firstDate, lastDate)) < 1;
}

/** Returns true if period between dates is 1m, else returns false */
function oneMinutePassed(firstDate, lastDate) {
  return Math.abs(differenceInMinutes(firstDate, lastDate)) === 1;
}

/** Returns true if period between dates is less than 1m, else returns false */
function diffLess1m(firstDate, lastDate) {
  return Math.abs(differenceInMinutes(firstDate, lastDate)) < 1;
}

/** Returns formated date
  * if date was less than 24h ago returns `24 minutes` or `Just now`, or `1 hour` etc
  * if date was more than 24h return full date with time
  * */
export function formatDate(date, dateTimeFormat = defaultDateTimeFormat) {
  const now = new Date();
  if (diffLess1m(date, now)) {
    return 'Just now';
  }
  if (oneMinutePassed(date, now)) {
    return `${Math.abs(differenceInMinutes(date, now))} minute ago`;
  }
  if (diffLess1h(date, now)) {
    return `${Math.abs(differenceInMinutes(date, now))} minutes ago`;
  }
  if (oneHourPassed(date, now)) {
    return `${Math.abs(differenceInHours(date, now))} hour ago`;
  }
  if (diffLess24h(date, now)) {
    return `${Math.abs(differenceInHours(date, now))} hours ago`;
  }
  return format(date, dateTimeFormat);
}

/**
  * Returns how much time ago was given date
 */
export function formatTimeAgoShort(date) {
  const now = new Date();
  if (diffLess1m(date, now)) {
    return '1M';
  }
  if (diffLess1h(date, now)) {
    return `${Math.abs(differenceInMinutes(date, now))}M`;
  }
  if (diffLess24h(date, new Date())) {
    return `${Math.abs(differenceInHours(date, now))}H`;
  }
  return `${Math.abs(differenceInDays(date, now))}D`;
}

/** Function that returns object value by key */
export function getKeyByValue(object, value) {
  return Object.keys(object).find(key => object[key] === value);
}

/** Function that returns string from scienitific to decimal
  * Example: scienitific = '5.376e-7', decimal = '0.0000005376';
 */
export function scientificToDecimal(num) {
  num = +num;// eslint-disable-line no-param-reassign
  const numberIsScientific = /\d+\.?\d*e[+-]*\d+/i.test(num);
  if (numberIsScientific) {
    const zero = '0';
    const [numPart, ePart] = String(Math.abs(num)).toLowerCase().split('e');
    let zerosCount = Math.abs(ePart);
    const [intPart, fractionalPart = ''] = numPart.split('.');
    const minusSign = num < 0;
    if (ePart < 0) {
      return `${minusSign ? '-' : ''}${zero}.${zero.repeat(zerosCount - 1)}${intPart}${fractionalPart}`; // eslint-disable-line
    }
    if (fractionalPart) {
      zerosCount -= fractionalPart.length + 1;
    }
    return `${minusSign ? '-' : ''}${intPart}${fractionalPart}${zero.repeat(zerosCount)}`;
  }
  const [, fractionalPart] = num.toString().split('.');
  const numHasOneDecimalDigit = fractionalPart && fractionalPart.length === 1;
  return numHasOneDecimalDigit ? num.toFixed(2) : num.toString();
}

/** Function separates integer part on discharge parts, adds sign '$',
  * truncates fractional part to 2 significant digits
 */
export function formatMoney(value, currencyCode) {
  /** Gets some integer number as string in `str` argument, returns index of first
    * significant number in that number(not 0).
    * example1: `str` === '123123', returns 0.`
    * example2: `str` === '000123123', returns 3.`
    * example3: `str` === '0000000', returns -1.`
  */
  function getIndexOfFirstSignificantNumber(str) {
    for (let i = 0; i < str.length; i += 1) {
      const currentNumberIsNotZero = str[i] !== '0';
      if (currentNumberIsNotZero) {
        return i;
      }
    }
    return -1;
  }

  /**
    * rounds numberStr to first significantDigits with length significantDigitsMaxCount
   */
  function roundNumberToSignificantDigits(num, significantDigitsMaxCount) {
    const [, fractionalPart] = num.toString().split('.');
    if (!fractionalPart) {
      return num;
    }
    const firstSignificantDigitIndex = getIndexOfFirstSignificantNumber(fractionalPart);
    if (firstSignificantDigitIndex < 0) {
      return 0;
    }
    return round(num, firstSignificantDigitIndex + significantDigitsMaxCount);
  }

  const currencySign = (currenciesTypes[currencyCode] || currenciesTypes.usd).sign;

  if (+value === 0) {
    return `${currencySign}0`;
  }
  let result;
  if (Number.isInteger(+value)) {
    result = Math.abs(value).toString();
  } else if (Math.abs(value) >= 10000) {
    result = round(Math.abs(value)).toString();
  } else {
    const significantDigitsMaxCount = 2;
    result = scientificToDecimal(Math.abs(value));
    result = roundNumberToSignificantDigits(result, significantDigitsMaxCount);
    result = scientificToDecimal(Math.abs(result));
  }
  let [resultIntegerPart, resultFractionalPart] = result.toString().split('.'); // eslint-disable-line prefer-const
  resultIntegerPart = resultIntegerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (resultFractionalPart) {
    return `${value >= 0 ? '' : minus}${currencySign}${resultIntegerPart}.${resultFractionalPart}`;
  }
  return `${value >= 0 ? '' : minus}${currencySign}${resultIntegerPart}`;
}

/** Function check Chrome browser */
export function isChrome() {
  return !!window.chrome;
}

/** Function for check on safari browser */
export function isSafari() {
  return /constructor/i.test(window.HTMLElement) || (function (p) { return p.toString() === "[object SafariRemoteNotification]"; })(!window['safari'] || (typeof safari !== 'undefined' && safari.pushNotification)); // eslint-disable-line
}

/** Function for get necessary limit */
export function getNecessaryLimit(count, onePageSize) {
  if (count === 0) {
    return onePageSize;
  }
  return Math.ceil(count / onePageSize) * onePageSize;
}

/** Function return loadMoreBtn max count  */
export function getAvailableCountForLoadingMore(totalRows, loadedValuesLength) {
  const difference = totalRows - loadedValuesLength;
  if (difference >= maxCountLoadingMoreBtn) {
    return maxCountLoadingMoreBtn;
  }
  return difference;
}

/** Function returns pressed char from keypressed event, ignores special chars */
export function getPressedChar(event) {
  if (event.which == null) { // IE
    if (event.keyCode < 32) return null; // special char
    return String.fromCharCode(event.keyCode);
  }

  if (event.which !== 0 && event.charCode !== 0) { // all expect IE
    if (event.which < 32) return null; // special char
    return String.fromCharCode(event.which); // another once
  }

  return null; // special char
}

/** Function return true if char is number, else false */
export function isNumber(char) {
  return !Number.isNaN(Number.parseInt(char, 10));
}

/** Function format email which is more than a certain number of characters */
export function formatEmail(val) {
  const parts = val.split('@');
  let leftPart = parts[0];
  let rightPart = parts[1];
  if (leftPart.length > 14) {
    const lastCharLeftPart = leftPart.substr(13, 1);
    if (lastCharLeftPart === '.') {
      leftPart = `${parts[0].substr(0, 14)}..`;
    } else {
      leftPart = `${parts[0].substr(0, 14)}...`;
    }
  }
  if (rightPart.length > 6) {
    const lastCharRightPart = rightPart.substr(5, 1);
    if (lastCharRightPart === '.') {
      rightPart = `${parts[1].substr(0, 6)}..`;
    } else {
      rightPart = `${parts[1].substr(0, 6)}...`;
    }
  }
  const newEmail = `${leftPart}@${rightPart}`;
  return newEmail;
}

/** Function gets channels array or null and returns String of channels
 * example: channels = ['Push', 'SMS']; returns 'Platform, Push, SMS';
*/
export function generateChannelsList(channels) {
  const platformChannelName = 'Platform';
  if (!channels) {
    return platformChannelName;
  }
  const arr = [...channels];
  arr.unshift(platformChannelName);
  return arr.join(', ');
}

/** Function return count entries */
export function formatCountEntries(val) {
  if (val === 1) {
    return `${val} entry`;
  }
  return `${val} entries...`;
}


/** Returns icon component according to passed brandName */
export function getCardIcon(brandName) {
  switch (brandName) {
    case 'Visa':
      return IconVisa;
    case 'MasterCard':
      return IconMasterCard;
    case 'American Express':
      return IconAmericanExpress;
    case 'Discover':
      return IconDiscover;
    case 'Diners Club':
      return IconDinersClub;
    case 'JCB':
      return IconJCB;
    case 'UnionPay':
      return IconUnionPay;
    default:
      return IconPayment;
  }
}

/** Function multiplies value on 100 and rounds,
 *  value to 2 digits after dot, and returns it.
 * examples: val = 0.1, returns 10;
 *           val = 0.07, returns 7
*/
export function formatPercentValue(val, multiplyTo100 = false) {
  return (+round(multiplyTo100 ? val * 100 : val, 2)).toString();
}

/** Formats percent and added sign as prefix.
 */
export function beautifyPercentValue(val) {
  const formattedPercent = formatPercentValue(val);
  if (formattedPercent >= 0) {
    return `+${formattedPercent}`;
  }
  return `${minus}${Math.abs(formattedPercent)}`;
}

/** Returns true if alert type is percentage, else - false;
 */
export function checkAlertTypeIsPercentage(alertType) {
  return alertType === percentageIncreaseName || alertType === percentageDecreaseName;
}

/** If alertType is percentage, multiplies val on 100.
 *  Result will be converted scientific to decimal
 */
export function beautifyTargetValue(val, alertType) {
  if (checkAlertTypeIsPercentage(alertType)) {
    return scientificToDecimal(formatPercentValue(val, true));
  }
  return scientificToDecimal(val);
}

/** Gets priceObj: {eur: number, usd: number}, and currencyCode
 *  Returns priceObj.usd or priceObj.eur regards currencyCode
 */
export function getValueForCurrencyCode(priceObj, currencyCode) {
  if (!priceObj) {
    return 0;
  }
  return priceObj[currencyCode] || priceObj.usd;
}

/**
  * Function for getting a prop of obj in a necessary currency
 */
export function getObjPropInSelectedCurrency(obj, propName, selectedCurrency) {
  return obj[`${propName}${selectedCurrency.toUpperCase()}`];
}

/** Gets server's value of valueType returns client's value of valueType
 */
export function beautifyValueType(valueType) {
  return alertsValueTypes.find(p => p.name === valueType).value;
}

/** Returns real page width in pixels
 */
export function getPageWidth() {
  return Math.max(
    document.body.scrollWidth,
    document.documentElement.scrollWidth,
    document.body.offsetWidth,
    document.documentElement.offsetWidth,
    document.documentElement.clientWidth,
  );
}

/** Returns true if current device is mobile
 */
export function checkCurrentDeviceIsMobile() {
  function mq(query) {
    return window.matchMedia(query).matches;
  }
  const prefixes = ' -webkit- -moz- -o- -ms- '.split(' ');

  if (('ontouchstart' in window) || (window.DocumentTouch && document instanceof window.DocumentTouch)) {
    return true;
  }

  // include the 'heartz' as a way to have a non matching MQ to help terminate the join
  // https://git.io/vznFH
  const query = ['(', prefixes.join('touch-enabled),('), 'heartz', ')'].join('');
  return mq(query);
}

/** Returns true if num has available precision, else - false;
  * IMPORTANT num should be a String
 */
export function checkNumHasAvailablePrecision(num, precision) {
  const numWithoutScientific = num.toString().includes('e') ? scientificToDecimal(num) : num.toString();
  const [, decimalPart] = numWithoutScientific.split('.');
  const numberIsInteger = !decimalPart || decimalPart.split('').every(sym => sym === '0');
  return numberIsInteger || decimalPart.length <= precision;
}

/** Returns beautified targetDelta according to alertType with units(%, $ etc)
  * default currency is usd
 */
export function beautifyTargetDeltaValueWithUnits(val, alertType, currencyCode) {
  const beautifulValue = beautifyTargetValue(val, alertType);
  if (checkAlertTypeIsPercentage(alertType)) {
    return `${beautifulValue}%`;
  }
  const currencySign = (currenciesTypes[currencyCode] || currenciesTypes.usd).sign;
  return `${currencySign}${beautifulValue}`;
}

/** Returns currency code from currency sign
  * if nothing found returns usd
 */
export function getCurrencyCodeBySign(sign) {
  const currenciesTypesArr = Object.values(currenciesTypes);
  const currType = currenciesTypesArr.find(ct => ct.sign === sign) || currenciesTypes.usd;
  return currType.code;
}

/** Returns true if targetDelta is valid */
export function targetDeltaValidator(targetDelta, valueParameter, alertTypeIsPercentage, alertTypeIsDecrease) { // eslint-disable-line max-len
  const alertIsFlatDecrease = !alertTypeIsPercentage && alertTypeIsDecrease;
  const alertIsPercentageDecrease = alertTypeIsPercentage && alertTypeIsDecrease;
  const percentageAlertHasError = (targetDelta >= 100) && alertIsPercentageDecrease;
  const flatAlertHasError = (+targetDelta >= +valueParameter) && alertIsFlatDecrease;
  return !(percentageAlertHasError || flatAlertHasError);
}



// WEBPACK FOOTER //
// ./src/utils/helpers.js